import { AssetsSvgMap, TAssetsSvgMapValue, TCommonData, TWidget, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { ChangeEvent, ChangeEventHandler, MouseEvent, MouseEventHandler, useId, useRef } from 'react';
import { SvgComponent } from '../../common/Svg/SvgComponent';
import { IMargin } from '../../interface/interface';
import { TConsumerInputPhoneData, TConsumerListData } from '../../types/data';
import { TListOption } from '../../types/options';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';
import styles from './styles/styles.module.css';

const focus = (e: MouseEvent<HTMLElement>) => {
    if (e.currentTarget) {
        const input = e.currentTarget.firstElementChild as HTMLElement;
        if (input) {
            input.focus();
        }
    }
};

interface TOwnProps extends IMargin {
    handleCloseClick?: () => void;
    searchPlaceholder?: string;
    isSearchInput?: boolean;
    uid?: string;
    backIcon?: TAssetsSvgMapValue;
}

type TInputSearchProps<TFunc = ChangeEventHandler> = (
    | {
          asButton: true;
          handleClick: MouseEventHandler;
      }
    | {
          asButton: false;
          value: string;
          handleChange?: TFunc;
          handleClickRightComponent?: MouseEventHandler;
          autoFocus?: boolean;
      }
) &
    TOwnProps &
    Partial<TWidgetProps<TConsumerListData & TConsumerInputPhoneData, TListOption>>;
const InputSearch = <T extends (e: ChangeEvent<HTMLInputElement>) => void>({
    handleCloseClick,
    backIcon = AssetsSvgMap.ic_arrow_back,
    ...props
}: TInputSearchProps<T>) => {
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const isShowRightInputComponent = !props.asButton && props.value.length > 0;
    const placeholder = props.searchPlaceholder ?? props.widget?.data?.messages?.searchPlaceholder;
    const uid = props.uid ?? props.widget?.uid;

    return (
        <InputWrapper
            widget={props.widget ?? ({} as TWidget<TCommonData>)}
            htmlFor={inputId}
            setFocus={focus}
            marginTop={props.marginTop ?? props.widget?.options?.margin?.top}
            marginRight={props.marginRight ?? props.widget?.options?.margin?.right}
            marginBottom={props.marginBottom ?? props.widget?.options?.margin?.bottom}
            marginLeft={props.marginLeft ?? props.widget?.options?.margin?.left}
            isSearchInput={props.isSearchInput}
            leftInputComponent={
                props.asButton ? (
                    <div className={styles.iconWrapper}>
                        <SvgComponent fill="grey_72_lc" name={AssetsSvgMap.ic_search} />
                    </div>
                ) : (
                    <div className={clsx(styles.iconWrapper, styles.isButtonIcon)} onClick={handleCloseClick}>
                        <SvgComponent fill="black" name={backIcon} />
                    </div>
                )
            }
            rightInputComponent={
                isShowRightInputComponent ? (
                    <span
                        className={styles.clearIcon}
                        onClick={(e) => {
                            props.handleClickRightComponent?.(e);
                            inputRef.current?.focus();
                        }}
                    >
                        <SvgComponent fill="grey_72_lc" name={AssetsSvgMap.ic_x_circle} />
                    </span>
                ) : null
            }
        >
            {props.asButton ? (
                <input placeholder={placeholder} type="search" data-test={uid} onClick={props.handleClick} />
            ) : (
                <input
                    id={inputId}
                    ref={inputRef}
                    onChange={props.handleChange}
                    value={props.value}
                    placeholder={placeholder}
                    type="search"
                    size={100}
                    autoComplete="off"
                    data-test={uid}
                    autoFocus={props.autoFocus}
                />
            )}
        </InputWrapper>
    );
};
export default InputSearch;
